import React, { useEffect, useMemo, useState } from "react";
import {
  AlertCircle,
  AlertTriangle,
  Calendar,
  CheckCircle2,
  ChevronLeft,
  ChevronRight,
  Clock,
  Copy,
  Eye,
  FileSpreadsheet,
  FileText,
  Filter,
  Info,
  Loader2,
  Mail,
  Play,
  RefreshCw,
  RotateCcw,
  ScrollText,
  Search,
  Sparkles,
  Users,
  X,
  Zap,
} from "lucide-react";
import { useAuth } from "../../contexts/AuthContext";
import { mailApi } from "../../services/mailApi";
import { safeJsonParseArray } from "./mailUtils";

const REPORT_LABELS = {
  raw_transitions: "Ham Geçişler Raporu",
  entry_exit_summary: "Giriş-Çıkış Özet Raporu",
  puantaj_raporu: "Puantaj Raporu",
  daily_attendance: "Günlük Devam Raporu",
  latecomers_report: "Geç Gelenler Raporu",
  early_leavers_report: "Erken Çıkanlar Raporu",
  smtp_test: "SMTP Bağlantı Testi",
};

function useMailPerms() {
  const { permissions = [] } = useAuth() || {};
  const has = (k) => permissions?.includes(k);
  return {
    canView:
      has("mail_dispatch_logs:view") ||
      has("mail_subscriptions:view") ||
      has("mail_subscriptions:manage"),
    canRun: has("mail_subscriptions:run") || has("mail_subscriptions:manage"),
  };
}

function fmtDateTime(v) {
  if (!v) return "-";
  try {
    const d = new Date(v);
    if (isNaN(d.getTime())) return String(v);
    return d.toLocaleString("tr-TR", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
    });
  } catch {
    return String(v);
  }
}

function formatBytes(bytes) {
  const b = Number(bytes || 0);
  if (!b || b <= 0) return "-";
  if (b < 1024) return `${b} B`;
  const kb = b / 1024;
  if (kb < 1024) return `${kb < 10 ? kb.toFixed(1) : Math.round(kb)} KB`;
  const mb = kb / 1024;
  return `${mb.toFixed(2)} MB`;
}

function formatDuration(startedAt, completedAt, durationMsProp) {
  let ms = durationMsProp;
  if ((ms === null || ms === undefined) && startedAt && completedAt) {
    const t1 = new Date(startedAt).getTime();
    const t2 = new Date(completedAt).getTime();
    if (!isNaN(t1) && !isNaN(t2) && t2 >= t1) {
      ms = t2 - t1;
    }
  }
  if (ms === null || ms === undefined || ms < 0) return "-";
  if (ms < 1000) return `${Math.max(1, Math.round(ms))} ms`;
  const sec = ms / 1000;
  if (sec < 60) return `${sec.toFixed(1)} sn`;
  const min = Math.floor(sec / 60);
  const remSec = Math.round(sec % 60);
  return `${min} dk ${remSec} sn`;
}

/**
 * Backend MailDispatchStatus enum karşılığı:
 * 1 = Queued (Kuyrukta)
 * 2 = Sent (Gönderildi)
 * 3 = Failed (Hata)
 * 4 = SkippedDuplicate (Mükerrer Atlandı)
 * 5 = NoData (Rapor Verisi Yok)
 */
function resolveStatusMeta(item) {
  const rawStatus = item?.statusName ?? item?.StatusName ?? item?.status ?? item?.Status ?? "";
  const errText = String(item?.errorMessage ?? item?.ErrorMessage ?? "");
  const n = typeof rawStatus === "number" ? rawStatus : Number(String(rawStatus).trim());
  const val = String(rawStatus || "").trim().toLowerCase();

  const isLegacyNoData =
    (n === 3 || val === "failed") &&
    (errText.toLowerCase().includes("rapor verisi bulunamadı") ||
      errText.toLowerCase().includes("veri bulunamadı"));

  if (isLegacyNoData || val === "nodata" || val === "no_data" || n === 5) {
    return {
      key: "NoData",
      text: "Veri Yok",
      cls: "bg-amber-50 text-amber-700 border-amber-200 dark:bg-amber-950/40 dark:text-amber-300 dark:border-amber-800/60",
      dotCls: "bg-amber-500",
      icon: AlertTriangle,
    };
  }

  if (val === "queued" || n === 1) {
    return {
      key: "Queued",
      text: "Kuyrukta",
      cls: "bg-sky-50 text-sky-700 border-sky-200 dark:bg-sky-950/40 dark:text-sky-300 dark:border-sky-800/60",
      dotCls: "bg-sky-500 animate-pulse",
      icon: Clock,
    };
  }

  if (val === "sent" || n === 2) {
    return {
      key: "Sent",
      text: "Gönderildi",
      cls: "bg-emerald-50 text-emerald-700 border-emerald-200 dark:bg-emerald-950/40 dark:text-emerald-300 dark:border-emerald-800/60",
      dotCls: "bg-emerald-500",
      icon: CheckCircle2,
    };
  }

  if (val === "failed" || n === 3) {
    return {
      key: "Failed",
      text: "Hata",
      cls: "bg-red-50 text-red-700 border-red-200 dark:bg-red-950/40 dark:text-red-300 dark:border-red-800/60",
      dotCls: "bg-red-500",
      icon: AlertCircle,
    };
  }

  if (val === "skipped" || val === "skippedduplicate" || n === 4) {
    return {
      key: "SkippedDuplicate",
      text: "Atlandı (Mükerrer)",
      cls: "bg-gray-100 text-gray-700 border-gray-200 dark:bg-gray-800 dark:text-gray-300 dark:border-gray-700",
      dotCls: "bg-gray-400",
      icon: Info,
    };
  }

  return {
    key: "Unknown",
    text: rawStatus === "" ? "-" : String(rawStatus),
    cls: "bg-gray-100 text-gray-700 border-gray-200 dark:bg-gray-800 dark:text-gray-300 dark:border-gray-700",
    dotCls: "bg-gray-400",
    icon: Info,
  };
}

function reportFormatBadge(v) {
  if (v === null || v === undefined) return null;
  const s = String(v).trim().toLowerCase();
  if (s === "1" || s === "pdf") {
    return {
      label: "PDF",
      cls: "bg-rose-50 text-rose-700 border-rose-200 dark:bg-rose-950/40 dark:text-rose-300 dark:border-rose-800/60",
      icon: FileText,
    };
  }
  if (s === "2" || s === "excel" || s === "xlsx") {
    return {
      label: "Excel",
      cls: "bg-emerald-50 text-emerald-700 border-emerald-200 dark:bg-emerald-950/40 dark:text-emerald-300 dark:border-emerald-800/60",
      icon: FileSpreadsheet,
    };
  }
  if (s === "3" || s === "pdfandexcel" || s === "both" || s === "pdf+excel") {
    return {
      label: "PDF + Excel",
      cls: "bg-indigo-50 text-indigo-700 border-indigo-200 dark:bg-indigo-950/40 dark:text-indigo-300 dark:border-indigo-800/60",
      icon: FileSpreadsheet,
    };
  }
  return null;
}

function parseRunKeyDetails(runKey) {
  if (!runKey) return { periodText: "-", isForce: false, isTest: false, raw: "-" };
  const s = String(runKey);
  const parts = s.split("|").map((x) => x.trim()).filter(Boolean);

  if (parts[0]?.toUpperCase() === "TEST") {
    return {
      periodText: parts[1] ? `Test Alıcısı: ${parts[1]}` : "SMTP Bağlantı Doğrulaması",
      isForce: true,
      isTest: true,
      raw: s,
    };
  }

  if (parts.length < 3) {
    return { periodText: s, isForce: false, isTest: false, raw: s };
  }

  const p1 = parts[2];
  const p2 = parts[3] && !parts[3].toLowerCase().startsWith("force") ? parts[3] : parts[2];
  const isForce = parts.some((p) => p.toLowerCase() === "force");

  const fmtYmd = (ymd) => {
    const m = String(ymd || "").match(/^(\d{4})(\d{2})(\d{2})$/);
    if (!m) return String(ymd || "");
    return `${m[3]}.${m[2]}.${m[1]}`;
  };

  const periodText = p1 === p2 ? fmtYmd(p1) : `${fmtYmd(p1)} → ${fmtYmd(p2)}`;
  return {
    periodText,
    isForce,
    isTest: false,
    raw: s,
  };
}

export default function MailDispatchLogsTab() {
  const perms = useMailPerms();

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [toast, setToast] = useState(null);
  const [items, setItems] = useState([]);

  // Filtreler
  const [statusFilter, setStatusFilter] = useState("");
  const [subscriptionFilter, setSubscriptionFilter] = useState("");
  const [triggerFilter, setTriggerFilter] = useState(""); // "" | "auto" | "manual" | "test"
  const [searchQuery, setSearchQuery] = useState("");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");

  // Sayfalama
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(15);

  // Detay Modalı & Tekrar Gönderme
  const [selectedLog, setSelectedLog] = useState(null);
  const [retryingSubId, setRetryingSubId] = useState(null);

  function showToast(type, message) {
    setToast({ type, message });
    setTimeout(() => {
      setToast((prev) => (prev?.message === message ? null : prev));
    }, 4000);
  }

  async function load() {
    if (!perms.canView) {
      setLoading(false);
      return;
    }
    setLoading(true);
    setError("");
    try {
      const data = await mailApi.listDispatchLogs({
        take: 300,
        startDate: startDate || undefined,
        endDate: endDate || undefined,
      });
      const list = Array.isArray(data?.items)
        ? data.items
        : Array.isArray(data)
        ? data
        : [];
      setItems(list);
    } catch (e) {
      setError(e?.message || "Mail gönderim logları yüklenemedi.");
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    if (!perms.canView) {
      setLoading(false);
      return;
    }
    load();
  }, [perms.canView, startDate, endDate]);

  // Mevcut loglardaki benzersiz abonelik seçenekleri
  const subscriptionOptions = useMemo(() => {
    const map = new Map();
    for (const x of items || []) {
      const subId = x.subscriptionId ?? x.SubscriptionId;
      if (subId === undefined || subId === null) continue;
      const subName =
        x.subscriptionName ??
        x.SubscriptionName ??
        (subId === 0 ? "SMTP Bağlantı Testi" : `Abonelik #${subId}`);
      if (!map.has(String(subId))) {
        map.set(String(subId), { id: String(subId), name: subName });
      }
    }
    return Array.from(map.values());
  }, [items]);

  // İstatistikler (KPI)
  const kpi = useMemo(() => {
    let total = 0;
    let sent = 0;
    let noData = 0;
    let failed = 0;
    let queued = 0;
    let totalBytes = 0;

    for (const x of items || []) {
      total++;
      const meta = resolveStatusMeta(x);
      if (meta.key === "Sent") sent++;
      else if (meta.key === "NoData") noData++;
      else if (meta.key === "Failed") failed++;
      else if (meta.key === "Queued") queued++;

      const bytes = Number(
        x.attachmentSizeBytes ??
          x.AttachmentSizeBytes ??
          x.attachmentSize ??
          x.AttachmentSize ??
          0
      );
      if (bytes > 0) totalBytes += bytes;
    }

    const successRate = total > 0 ? Math.round(((sent + noData) / total) * 100) : 0;
    return { total, sent, noData, failed, queued, totalBytes, successRate };
  }, [items]);

  // Filtrelenmiş ve Sıralanmış Kayıtlar
  const filteredItems = useMemo(() => {
    const q = searchQuery.trim().toLowerCase();

    return [...(items || [])]
      .filter((x) => {
        const meta = resolveStatusMeta(x);
        const subId = String(x.subscriptionId ?? x.SubscriptionId ?? "");
        const runKey = String(x.runKey ?? x.RunKey ?? "");
        const parsedKey = parseRunKeyDetails(runKey);
        const isTest = Boolean(x.isTestMail ?? x.IsTestMail ?? (subId === "0" || parsedKey.isTest));
        const isManual = Boolean(x.isManualTrigger ?? x.IsManualTrigger ?? parsedKey.isForce);

        if (statusFilter && meta.key.toLowerCase() !== statusFilter.toLowerCase()) {
          return false;
        }

        if (subscriptionFilter !== "" && subId !== subscriptionFilter) {
          return false;
        }

        if (triggerFilter === "test" && !isTest) return false;
        if (triggerFilter === "manual" && (!isManual || isTest)) return false;
        if (triggerFilter === "auto" && (isManual || isTest)) return false;

        if (q) {
          const subName = String(x.subscriptionName ?? x.SubscriptionName ?? "").toLowerCase();
          const repType = String(x.reportType ?? x.ReportType ?? "").toLowerCase();
          const repLabel = String(REPORT_LABELS[repType] || "").toLowerCase();
          const err = String(x.errorMessage ?? x.ErrorMessage ?? "").toLowerCase();
          const toJson = String(x.recipientsToJson ?? x.RecipientsToJson ?? "").toLowerCase();
          const ccJson = String(x.recipientsCcJson ?? x.RecipientsCcJson ?? "").toLowerCase();

          const matched =
            subName.includes(q) ||
            repType.includes(q) ||
            repLabel.includes(q) ||
            runKey.toLowerCase().includes(q) ||
            err.includes(q) ||
            toJson.includes(q) ||
            ccJson.includes(q);

          if (!matched) return false;
        }

        return true;
      })
      .sort((a, b) => {
        const idA = Number(a.id ?? a.Id ?? 0);
        const idB = Number(b.id ?? b.Id ?? 0);
        if (idA && idB) return idB - idA;
        const da = new Date(a.startedAt ?? a.StartedAt ?? 0).getTime();
        const db = new Date(b.startedAt ?? b.StartedAt ?? 0).getTime();
        return db - da;
      });
  }, [items, statusFilter, subscriptionFilter, triggerFilter, searchQuery]);

  // Filtre değiştiğinde 1. sayfaya dön
  useEffect(() => {
    setPage(1);
  }, [statusFilter, subscriptionFilter, triggerFilter, searchQuery, startDate, endDate, pageSize]);

  const totalPages = Math.max(1, Math.ceil(filteredItems.length / pageSize));
  const pagedItems = useMemo(() => {
    const start = (page - 1) * pageSize;
    return filteredItems.slice(start, start + pageSize);
  }, [filteredItems, page, pageSize]);

  async function handleCopy(text, label = "Kopyalandı") {
    try {
      await navigator.clipboard.writeText(String(text || ""));
      showToast("success", `${label} panoya kopyalandı.`);
    } catch {
      // ignore
    }
  }

  async function handleRetrySubscription(subId, subName) {
    if (!subId || Number(subId) <= 0) return;
    setRetryingSubId(subId);
    try {
      await mailApi.runNow(subId);
      showToast("success", `"${subName}" aboneliği yeniden gönderim kuyruğuna eklendi.`);
      setTimeout(() => {
        load();
      }, 1500);
    } catch (e) {
      showToast("error", e?.message || "Yeniden gönderim başlatılamadı.");
    } finally {
      setRetryingSubId(null);
    }
  }

  function clearFilters() {
    setStatusFilter("");
    setSubscriptionFilter("");
    setTriggerFilter("");
    setSearchQuery("");
    setStartDate("");
    setEndDate("");
  }

  if (!perms.canView) {
    return (
      <div className="p-6 rounded-2xl border border-amber-200 dark:border-amber-800/50 bg-amber-50/50 dark:bg-amber-950/20 text-amber-700 dark:text-amber-300 flex items-center gap-3">
        <AlertTriangle className="w-5 h-5 shrink-0" />
        <span className="text-sm font-medium">Mail gönderim loglarını görüntüleme yetkiniz bulunmamaktadır.</span>
      </div>
    );
  }

  const hasActiveFilters =
    Boolean(statusFilter) ||
    Boolean(subscriptionFilter) ||
    Boolean(triggerFilter) ||
    Boolean(searchQuery) ||
    Boolean(startDate) ||
    Boolean(endDate);

  return (
    <div className="space-y-5">
      {/* ÜST BAŞLIK & HIZLI AKSİYONLAR */}
      <div className="bg-white dark:bg-[#18191c] border border-gray-200 dark:border-[#212226] rounded-2xl p-5 shadow-sm">
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
          <div className="flex items-start gap-3.5">
            <div className="w-11 h-11 rounded-2xl bg-emerald-500/10 border border-emerald-500/20 flex items-center justify-center text-emerald-600 dark:text-emerald-400 shrink-0">
              <ScrollText className="w-5 h-5" />
            </div>
            <div>
              <div className="flex items-center gap-2.5 flex-wrap">
                <h2 className="text-lg font-bold text-gray-900 dark:text-white">
                  Mail Gönderim Logları & Denetim İzleri
                </h2>
                <span className="px-2.5 py-0.5 rounded-full text-[11px] font-semibold bg-emerald-50 text-emerald-700 dark:bg-emerald-950/50 dark:text-emerald-300 border border-emerald-200 dark:border-emerald-800/60">
                  Canlı İzleme
                </span>
              </div>
              <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                Otomatik zamanlayıcı (Cron), manuel tetikleme ve SMTP bağlantı testlerine ait tüm e-posta gönderim geçmişini, ek boyutlarını ve hata raporlarını inceleyin.
              </p>
            </div>
          </div>

          <div className="flex items-center gap-2 shrink-0">
            {hasActiveFilters && (
              <button
                type="button"
                onClick={clearFilters}
                className="inline-flex items-center gap-1.5 px-3 py-2 rounded-xl text-xs font-semibold text-gray-600 dark:text-gray-300 bg-gray-100 dark:bg-[#111214] hover:bg-gray-200 dark:hover:bg-gray-800 border border-gray-200 dark:border-[#212226] transition-all"
              >
                <RotateCcw className="w-3.5 h-3.5" />
                Filtreleri Sıfırla
              </button>
            )}
            <button
              type="button"
              onClick={load}
              disabled={loading}
              className="inline-flex items-center gap-2 px-4 py-2 rounded-xl text-xs font-semibold bg-emerald-600 hover:bg-emerald-700 text-white shadow-sm shadow-emerald-600/20 transition-all disabled:opacity-60"
            >
              <RefreshCw className={`w-3.5 h-3.5 ${loading ? "animate-spin" : ""}`} />
              Yenile
            </button>
          </div>
        </div>

        {/* 5 ÖZET KPI KARTI */}
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3 mt-5">
          <button
            type="button"
            onClick={() => setStatusFilter("")}
            className={`text-left p-3.5 rounded-xl border transition-all ${
              statusFilter === ""
                ? "bg-gray-50 dark:bg-[#111214] border-emerald-500/40 ring-1 ring-emerald-500/20"
                : "bg-gray-50/60 dark:bg-[#111214]/60 border-gray-200 dark:border-[#212226] hover:border-gray-300"
            }`}
          >
            <div className="text-[11px] font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400">
              Toplam Kayıt
            </div>
            <div className="text-2xl font-bold text-gray-900 dark:text-white mt-1">
              {kpi.total}
            </div>
            <div className="text-[11px] text-gray-500 dark:text-gray-400 mt-0.5">
              Tamamlanma: %{kpi.successRate}
            </div>
          </button>

          <button
            type="button"
            onClick={() => setStatusFilter(statusFilter === "Sent" ? "" : "Sent")}
            className={`text-left p-3.5 rounded-xl border transition-all ${
              statusFilter === "Sent"
                ? "bg-emerald-50/80 dark:bg-emerald-950/30 border-emerald-500 ring-1 ring-emerald-500/30"
                : "bg-gray-50/60 dark:bg-[#111214]/60 border-gray-200 dark:border-[#212226] hover:border-emerald-500/40"
            }`}
          >
            <div className="flex items-center justify-between">
              <span className="text-[11px] font-semibold uppercase tracking-wider text-emerald-600 dark:text-emerald-400">
                Gönderildi
              </span>
              <CheckCircle2 className="w-4 h-4 text-emerald-500" />
            </div>
            <div className="text-2xl font-bold text-emerald-600 dark:text-emerald-400 mt-1">
              {kpi.sent}
            </div>
            <div className="text-[11px] text-gray-500 dark:text-gray-400 mt-0.5">
              Başarılı e-posta
            </div>
          </button>

          <button
            type="button"
            onClick={() => setStatusFilter(statusFilter === "NoData" ? "" : "NoData")}
            className={`text-left p-3.5 rounded-xl border transition-all ${
              statusFilter === "NoData"
                ? "bg-amber-50/80 dark:bg-amber-950/30 border-amber-500 ring-1 ring-amber-500/30"
                : "bg-gray-50/60 dark:bg-[#111214]/60 border-gray-200 dark:border-[#212226] hover:border-amber-500/40"
            }`}
          >
            <div className="flex items-center justify-between">
              <span className="text-[11px] font-semibold uppercase tracking-wider text-amber-600 dark:text-amber-400">
                Veri Yok
              </span>
              <AlertTriangle className="w-4 h-4 text-amber-500" />
            </div>
            <div className="text-2xl font-bold text-amber-600 dark:text-amber-400 mt-1">
              {kpi.noData}
            </div>
            <div className="text-[11px] text-gray-500 dark:text-gray-400 mt-0.5">
              Dönemde kayıt çıkmadı
            </div>
          </button>

          <button
            type="button"
            onClick={() => setStatusFilter(statusFilter === "Failed" ? "" : "Failed")}
            className={`text-left p-3.5 rounded-xl border transition-all ${
              statusFilter === "Failed"
                ? "bg-red-50/80 dark:bg-red-950/30 border-red-500 ring-1 ring-red-500/30"
                : "bg-gray-50/60 dark:bg-[#111214]/60 border-gray-200 dark:border-[#212226] hover:border-red-500/40"
            }`}
          >
            <div className="flex items-center justify-between">
              <span className="text-[11px] font-semibold uppercase tracking-wider text-red-600 dark:text-red-400">
                Hata Alan
              </span>
              <AlertCircle className="w-4 h-4 text-red-500" />
            </div>
            <div className="text-2xl font-bold text-red-600 dark:text-red-400 mt-1">
              {kpi.failed}
            </div>
            <div className="text-[11px] text-gray-500 dark:text-gray-400 mt-0.5">
              SMTP / yapılandırma hatası
            </div>
          </button>

          <div className="p-3.5 rounded-xl border bg-gray-50/60 dark:bg-[#111214]/60 border-gray-200 dark:border-[#212226] col-span-2 sm:col-span-1">
            <div className="flex items-center justify-between">
              <span className="text-[11px] font-semibold uppercase tracking-wider text-indigo-600 dark:text-indigo-400">
                Toplam Ek Boyutu
              </span>
              <FileSpreadsheet className="w-4 h-4 text-indigo-500" />
            </div>
            <div className="text-2xl font-bold text-gray-900 dark:text-white mt-1">
              {formatBytes(kpi.totalBytes)}
            </div>
            <div className="text-[11px] text-gray-500 dark:text-gray-400 mt-0.5">
              PDF & Excel rapor ekleri
            </div>
          </div>
        </div>
      </div>

      {/* BİLDİRİM TOAST */}
      {toast && (
        <div
          className={`p-3.5 rounded-xl border text-xs font-semibold flex items-center justify-between shadow-sm ${
            toast.type === "error"
              ? "bg-red-50 dark:bg-red-950/40 text-red-700 dark:text-red-300 border-red-200 dark:border-red-800"
              : "bg-emerald-50 dark:bg-emerald-950/40 text-emerald-700 dark:text-emerald-300 border-emerald-200 dark:border-emerald-800"
          }`}
        >
          <div className="flex items-center gap-2">
            {toast.type === "error" ? (
              <AlertCircle className="w-4 h-4 shrink-0" />
            ) : (
              <CheckCircle2 className="w-4 h-4 shrink-0" />
            )}
            <span>{toast.message}</span>
          </div>
          <button
            type="button"
            onClick={() => setToast(null)}
            className="p-1 rounded-lg hover:bg-black/5 dark:hover:bg-white/5"
          >
            <X className="w-3.5 h-3.5" />
          </button>
        </div>
      )}

      {error && (
        <div className="p-4 text-xs font-medium rounded-xl bg-red-50 dark:bg-red-950/40 text-red-700 dark:text-red-300 border border-red-200 dark:border-red-800 flex items-center gap-2.5">
          <AlertCircle className="w-4 h-4 shrink-0" />
          <span>{error}</span>
        </div>
      )}

      {/* GELİŞMİŞ FİLTRE ÇUBUĞU */}
      <div className="bg-white dark:bg-[#18191c] border border-gray-200 dark:border-[#212226] rounded-2xl p-4 shadow-sm">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-6 gap-3">
          {/* Arama Kutusu */}
          <div className="lg:col-span-2 relative">
            <Search className="w-4 h-4 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none" />
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Abonelik adı, alıcı e-posta, kod veya hata ara..."
              className="w-full pl-9 pr-8 py-2 text-xs rounded-xl border border-gray-200 dark:border-[#212226] bg-gray-50 dark:bg-[#111214] text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-emerald-500/30 focus:border-emerald-500"
            />
            {searchQuery && (
              <button
                type="button"
                onClick={() => setSearchQuery("")}
                className="absolute right-2.5 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
              >
                <X className="w-3.5 h-3.5" />
              </button>
            )}
          </div>

          {/* Abonelik Filtresi */}
          <div>
            <select
              value={subscriptionFilter}
              onChange={(e) => setSubscriptionFilter(e.target.value)}
              className="w-full px-3 py-2 text-xs rounded-xl border border-gray-200 dark:border-[#212226] bg-gray-50 dark:bg-[#111214] text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-emerald-500/30"
            >
              <option value="">Tüm Abonelikler</option>
              {subscriptionOptions.map((sub) => (
                <option key={sub.id} value={sub.id}>
                  {sub.name}
                </option>
              ))}
            </select>
          </div>

          {/* Durum Filtresi */}
          <div>
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="w-full px-3 py-2 text-xs rounded-xl border border-gray-200 dark:border-[#212226] bg-gray-50 dark:bg-[#111214] text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-emerald-500/30"
            >
              <option value="">Tüm Durumlar</option>
              <option value="Sent">Gönderildi (Başarılı)</option>
              <option value="Failed">Hata Alanlar</option>
              <option value="NoData">Veri Yok (Boş Dönem)</option>
              <option value="Queued">Kuyrukta Bekleyen</option>
              <option value="SkippedDuplicate">Atlandı (Mükerrer)</option>
            </select>
          </div>

          {/* Tetikleme Tipi */}
          <div>
            <select
              value={triggerFilter}
              onChange={(e) => setTriggerFilter(e.target.value)}
              className="w-full px-3 py-2 text-xs rounded-xl border border-gray-200 dark:border-[#212226] bg-gray-50 dark:bg-[#111214] text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-emerald-500/30"
            >
              <option value="">Tüm Tetiklemeler</option>
              <option value="auto">Otomatik Zamanlayıcı (Cron)</option>
              <option value="manual">Manuel Tetikleme</option>
              <option value="test">SMTP Test Mailleri</option>
            </select>
          </div>

          {/* Tarih Filtresi */}
          <div className="flex items-center gap-1.5">
            <input
              type="date"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
              title="Başlangıç Tarihi"
              className="w-1/2 px-2 py-2 text-[11px] rounded-xl border border-gray-200 dark:border-[#212226] bg-gray-50 dark:bg-[#111214] text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-emerald-500/30"
            />
            <input
              type="date"
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
              title="Bitiş Tarihi"
              className="w-1/2 px-2 py-2 text-[11px] rounded-xl border border-gray-200 dark:border-[#212226] bg-gray-50 dark:bg-[#111214] text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-emerald-500/30"
            />
          </div>
        </div>
      </div>

      {/* LOG TABLOSU */}
      <div className="bg-white dark:bg-[#18191c] border border-gray-200 dark:border-[#212226] rounded-2xl shadow-sm overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-left border-collapse">
            <thead>
              <tr className="bg-gray-50/80 dark:bg-[#111214] border-b border-gray-200 dark:border-[#212226] text-[11px] font-bold uppercase tracking-wider text-gray-500 dark:text-gray-400">
                <th className="py-3.5 px-4">Durum & Tip</th>
                <th className="py-3.5 px-4">Abonelik & Rapor</th>
                <th className="py-3.5 px-4">Rapor Dönemi</th>
                <th className="py-3.5 px-4">Alıcılar (To / CC)</th>
                <th className="py-3.5 px-4">Başlangıç & Süre</th>
                <th className="py-3.5 px-4 text-right">Ek Dosya</th>
                <th className="py-3.5 px-4">Açıklama / Hata Detayı</th>
                <th className="py-3.5 px-4 text-right">İşlem</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100 dark:divide-[#212226] text-xs">
              {loading ? (
                <tr>
                  <td colSpan={8} className="py-12 text-center text-gray-500 dark:text-gray-400">
                    <Loader2 className="w-6 h-6 animate-spin inline-block text-emerald-500 mb-2" />
                    <div>Mail gönderim logları yükleniyor...</div>
                  </td>
                </tr>
              ) : pagedItems.length === 0 ? (
                <tr>
                  <td colSpan={8} className="py-12 text-center text-gray-500 dark:text-gray-400">
                    <Mail className="w-8 h-8 mx-auto mb-2 opacity-30" />
                    <div className="font-semibold text-gray-700 dark:text-gray-300">
                      Gösterilecek gönderim kaydı bulunamadı.
                    </div>
                    <div className="text-[11px] mt-1">
                      Seçili filtreleri değiştirebilir veya yeni bir mail aboneliği çalıştırabilirsiniz.
                    </div>
                  </td>
                </tr>
              ) : (
                pagedItems.map((x) => {
                  const id = x.id ?? x.Id;
                  const subId = x.subscriptionId ?? x.SubscriptionId ?? 0;
                  const runKey = x.runKey ?? x.RunKey ?? "";
                  const parsedKey = parseRunKeyDetails(runKey);
                  const statusMeta = resolveStatusMeta(x);
                  const StatusIcon = statusMeta.icon;

                  const isTest = Boolean(x.isTestMail ?? x.IsTestMail ?? (subId === 0 || parsedKey.isTest));
                  const isManual = Boolean(x.isManualTrigger ?? x.IsManualTrigger ?? parsedKey.isForce);

                  const subName =
                    x.subscriptionName ??
                    x.SubscriptionName ??
                    (isTest ? "SMTP Bağlantı Testi" : `Abonelik #${subId}`);
                  const subDeleted = Boolean(x.subscriptionIsDeleted ?? x.SubscriptionIsDeleted);

                  const reportType = x.reportType ?? x.ReportType ?? (isTest ? "smtp_test" : "");
                  const reportLabel = REPORT_LABELS[reportType] || reportType || "-";
                  const fmtBadge = reportFormatBadge(x.reportFormat ?? x.ReportFormat);

                  const toList = safeJsonParseArray(x.recipientsToJson ?? x.RecipientsToJson);
                  const ccList = safeJsonParseArray(x.recipientsCcJson ?? x.RecipientsCcJson);

                  const startedAt = x.startedAt ?? x.StartedAt;
                  const completedAt = x.completedAt ?? x.CompletedAt;
                  const durationStr = formatDuration(startedAt, completedAt, x.durationMs ?? x.DurationMs);

                  const attachmentCount = x.attachmentCount ?? x.AttachmentCount ?? 0;
                  const attachmentBytes =
                    x.attachmentSizeBytes ??
                    x.AttachmentSizeBytes ??
                    x.attachmentSize ??
                    x.AttachmentSize ??
                    0;

                  const err = x.errorMessage ?? x.ErrorMessage;

                  return (
                    <tr
                      key={id || runKey}
                      className="hover:bg-gray-50/80 dark:hover:bg-[#111214]/60 transition-colors"
                    >
                      {/* 1. Durum & Tetikleme Tipi */}
                      <td className="py-3.5 px-4 whitespace-nowrap">
                        <div className="flex flex-col items-start gap-1">
                          <span
                            className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[11px] font-semibold border ${statusMeta.cls}`}
                          >
                            <StatusIcon className="w-3.5 h-3.5 shrink-0" />
                            {statusMeta.text}
                          </span>
                          <span className="inline-flex items-center gap-1 text-[10px] font-medium text-gray-500 dark:text-gray-400 pl-1">
                            {isTest ? (
                              <>
                                <Sparkles className="w-3 h-3 text-purple-500" />
                                SMTP Testi
                              </>
                            ) : isManual ? (
                              <>
                                <Zap className="w-3 h-3 text-amber-500" />
                                Manuel Tetikleme
                              </>
                            ) : (
                              <>
                                <Clock className="w-3 h-3 text-emerald-500" />
                                Otomatik (Cron)
                              </>
                            )}
                          </span>
                        </div>
                      </td>

                      {/* 2. Abonelik & Rapor Türü */}
                      <td className="py-3.5 px-4">
                        <div className="flex flex-col gap-1 min-w-[170px]">
                          <div className="flex items-center gap-1.5">
                            <span className="font-bold text-gray-900 dark:text-white truncate max-w-[220px]">
                              {subName}
                            </span>
                            {subDeleted && (
                              <span className="px-1.5 py-0.5 text-[9px] font-semibold rounded bg-gray-200 dark:bg-gray-700 text-gray-600 dark:text-gray-300">
                                Silinmiş
                              </span>
                            )}
                          </div>
                          <div className="flex items-center gap-1.5 flex-wrap">
                            <span className="text-[11px] text-gray-500 dark:text-gray-400">
                              {reportLabel}
                            </span>
                            {fmtBadge && (
                              <span
                                className={`px-1.5 py-0.5 text-[10px] font-semibold rounded border ${fmtBadge.cls}`}
                              >
                                {fmtBadge.label}
                              </span>
                            )}
                          </div>
                        </div>
                      </td>

                      {/* 3. Rapor Dönemi & RunKey */}
                      <td className="py-3.5 px-4 whitespace-nowrap">
                        <div className="flex items-center gap-1.5">
                          <div>
                            <div className="font-semibold text-gray-800 dark:text-gray-200 flex items-center gap-1">
                              <Calendar className="w-3.5 h-3.5 text-gray-400 shrink-0" />
                              <span>{parsedKey.periodText}</span>
                            </div>
                            <div
                              className="text-[10px] text-gray-400 dark:text-gray-500 font-mono truncate max-w-[160px]"
                              title={runKey}
                            >
                              {runKey || "-"}
                            </div>
                          </div>
                          {runKey && (
                            <button
                              type="button"
                              onClick={() => handleCopy(runKey, "Çalıştırma kodu")}
                              className="p-1.5 rounded-lg text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
                              title="Çalıştırma Kodunu Kopyala"
                            >
                              <Copy className="w-3.5 h-3.5" />
                            </button>
                          )}
                        </div>
                      </td>

                      {/* 4. Alıcılar (To / CC) */}
                      <td className="py-3.5 px-4">
                        {toList.length === 0 && ccList.length === 0 ? (
                          <span className="text-gray-400">-</span>
                        ) : (
                          <div className="flex flex-col gap-0.5 max-w-[210px]">
                            <div className="text-[11px] font-medium text-gray-800 dark:text-gray-200 truncate">
                              {toList.slice(0, 2).join(", ")}
                            </div>
                            <div className="flex items-center gap-1.5 text-[10px] text-gray-500 dark:text-gray-400">
                              <Users className="w-3 h-3 shrink-0" />
                              <span>{toList.length} Alıcı (To)</span>
                              {toList.length > 2 && (
                                <span className="px-1.5 py-0.2 rounded bg-gray-100 dark:bg-gray-800 font-semibold">
                                  +{toList.length - 2}
                                </span>
                              )}
                              {ccList.length > 0 && <span>• {ccList.length} CC</span>}
                            </div>
                          </div>
                        )}
                      </td>

                      {/* 5. Başlangıç & Süre */}
                      <td className="py-3.5 px-4 whitespace-nowrap">
                        <div className="text-gray-800 dark:text-gray-200 font-medium">
                          {fmtDateTime(startedAt)}
                        </div>
                        <div className="text-[10px] text-gray-500 dark:text-gray-400 flex items-center gap-1 mt-0.5">
                          <span>Süre:</span>
                          <span className="font-semibold text-gray-700 dark:text-gray-300">
                            {durationStr}
                          </span>
                        </div>
                      </td>

                      {/* 6. Ek Dosya */}
                      <td className="py-3.5 px-4 text-right whitespace-nowrap">
                        {attachmentCount > 0 ? (
                          <div className="inline-flex flex-col items-end">
                            <span className="font-semibold text-gray-900 dark:text-white">
                              {attachmentCount} dosya
                            </span>
                            <span className="text-[10px] font-medium text-emerald-600 dark:text-emerald-400">
                              {formatBytes(attachmentBytes)}
                            </span>
                          </div>
                        ) : (
                          <span className="text-gray-400">-</span>
                        )}
                      </td>

                      {/* 7. Açıklama / Hata */}
                      <td className="py-3.5 px-4 max-w-[260px]">
                        {err ? (
                          <div
                            className={`text-[11px] line-clamp-2 ${
                              statusMeta.key === "NoData"
                                ? "text-amber-700 dark:text-amber-300"
                                : "text-red-600 dark:text-red-400 font-medium"
                            }`}
                            title={err}
                          >
                            {err}
                          </div>
                        ) : statusMeta.key === "Sent" ? (
                          <span className="text-[11px] text-emerald-600 dark:text-emerald-400 font-medium">
                            Rapor başarıyla oluşturuldu ve alıcılara iletildi.
                          </span>
                        ) : (
                          <span className="text-gray-400">-</span>
                        )}
                      </td>

                      {/* 8. İşlemler (Detay & Tekrar Gönder) */}
                      <td className="py-3.5 px-4 text-right whitespace-nowrap">
                        <div className="inline-flex items-center justify-end gap-1.5">
                          <button
                            type="button"
                            onClick={() => setSelectedLog(x)}
                            className="inline-flex items-center gap-1 px-2.5 py-1.5 rounded-xl text-[11px] font-semibold bg-gray-100 dark:bg-[#111214] hover:bg-gray-200 dark:hover:bg-gray-800 text-gray-700 dark:text-gray-200 border border-gray-200 dark:border-[#212226] transition-colors"
                            title="Log Detaylarını İncele"
                          >
                            <Eye className="w-3.5 h-3.5" />
                            Detay
                          </button>

                          {perms.canRun && subId > 0 && !subDeleted && (
                            <button
                              type="button"
                              disabled={retryingSubId === subId}
                              onClick={() => handleRetrySubscription(subId, subName)}
                              className={`inline-flex items-center gap-1 px-2.5 py-1.5 rounded-xl text-[11px] font-semibold border transition-colors ${
                                statusMeta.key === "Failed"
                                  ? "bg-red-50 hover:bg-red-100 text-red-700 border-red-200 dark:bg-red-950/40 dark:hover:bg-red-900/50 dark:text-red-300 dark:border-red-800/60"
                                  : "bg-emerald-50 hover:bg-emerald-100 text-emerald-700 border-emerald-200 dark:bg-emerald-950/40 dark:hover:bg-emerald-900/50 dark:text-emerald-300 dark:border-emerald-800/60"
                              }`}
                              title="Bu aboneliği şimdi tekrar çalıştır"
                            >
                              {retryingSubId === subId ? (
                                <Loader2 className="w-3.5 h-3.5 animate-spin" />
                              ) : (
                                <Play className="w-3.5 h-3.5" />
                              )}
                              Tekrar Gönder
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>

        {/* ALT SAYFALAMA (PAGINATION) */}
        {!loading && filteredItems.length > 0 && (
          <div className="px-4 py-3 bg-gray-50/70 dark:bg-[#111214] border-t border-gray-200 dark:border-[#212226] flex flex-col sm:flex-row items-center justify-between gap-3 text-xs">
            <div className="flex items-center gap-2 text-gray-500 dark:text-gray-400">
              <span>
                Toplam <strong className="text-gray-900 dark:text-white">{filteredItems.length}</strong> kayıttan{" "}
                <strong className="text-gray-900 dark:text-white">
                  {(page - 1) * pageSize + 1} - {Math.min(page * pageSize, filteredItems.length)}
                </strong>{" "}
                arası gösteriliyor
              </span>
              <select
                value={pageSize}
                onChange={(e) => setPageSize(Number(e.target.value))}
                className="ml-2 px-2 py-1 rounded-lg border border-gray-200 dark:border-[#212226] bg-white dark:bg-[#18191c] text-gray-800 dark:text-gray-200 text-xs"
              >
                <option value={15}>15 / sayfa</option>
                <option value={30}>30 / sayfa</option>
                <option value={50}>50 / sayfa</option>
              </select>
            </div>

            <div className="flex items-center gap-1.5">
              <button
                type="button"
                disabled={page <= 1}
                onClick={() => setPage((p) => Math.max(1, p - 1))}
                className="p-1.5 rounded-lg border border-gray-200 dark:border-[#212226] bg-white dark:bg-[#18191c] text-gray-700 dark:text-gray-300 disabled:opacity-40 hover:bg-gray-100 dark:hover:bg-gray-800"
              >
                <ChevronLeft className="w-4 h-4" />
              </button>
              <span className="px-3 py-1 font-semibold text-gray-800 dark:text-gray-200">
                Sayfa {page} / {totalPages}
              </span>
              <button
                type="button"
                disabled={page >= totalPages}
                onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                className="p-1.5 rounded-lg border border-gray-200 dark:border-[#212226] bg-white dark:bg-[#18191c] text-gray-700 dark:text-gray-300 disabled:opacity-40 hover:bg-gray-100 dark:hover:bg-gray-800"
              >
                <ChevronRight className="w-4 h-4" />
              </button>
            </div>
          </div>
        )}
      </div>

      {/* LOG DETAY MODALI */}
      {selectedLog && (
        <LogDetailModal
          log={selectedLog}
          onClose={() => setSelectedLog(null)}
          onCopy={handleCopy}
          canRun={perms.canRun}
          onRetry={handleRetrySubscription}
          retryingSubId={retryingSubId}
        />
      )}
    </div>
  );
}

function LogDetailModal({ log, onClose, onCopy, canRun, onRetry, retryingSubId }) {
  const subId = log.subscriptionId ?? log.SubscriptionId ?? 0;
  const runKey = log.runKey ?? log.RunKey ?? "";
  const parsedKey = parseRunKeyDetails(runKey);
  const statusMeta = resolveStatusMeta(log);
  const StatusIcon = statusMeta.icon;

  const isTest = Boolean(log.isTestMail ?? log.IsTestMail ?? (subId === 0 || parsedKey.isTest));
  const isManual = Boolean(log.isManualTrigger ?? log.IsManualTrigger ?? parsedKey.isForce);

  const subName =
    log.subscriptionName ??
    log.SubscriptionName ??
    (isTest ? "SMTP Bağlantı Testi" : `Abonelik #${subId}`);
  const subDeleted = Boolean(log.subscriptionIsDeleted ?? log.SubscriptionIsDeleted);

  const reportType = log.reportType ?? log.ReportType ?? (isTest ? "smtp_test" : "");
  const reportLabel = REPORT_LABELS[reportType] || reportType || "-";
  const fmtBadge = reportFormatBadge(log.reportFormat ?? log.ReportFormat);

  const toList = safeJsonParseArray(log.recipientsToJson ?? log.RecipientsToJson);
  const ccList = safeJsonParseArray(log.recipientsCcJson ?? log.RecipientsCcJson);

  const startedAt = log.startedAt ?? log.StartedAt;
  const completedAt = log.completedAt ?? log.CompletedAt;
  const durationStr = formatDuration(startedAt, completedAt, log.durationMs ?? log.DurationMs);

  const attachmentCount = log.attachmentCount ?? log.AttachmentCount ?? 0;
  const attachmentBytes =
    log.attachmentSizeBytes ??
    log.AttachmentSizeBytes ??
    log.attachmentSize ??
    log.AttachmentSize ??
    0;

  const err = log.errorMessage ?? log.ErrorMessage;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4">
      <div className="bg-white dark:bg-[#18191c] border border-gray-200 dark:border-[#212226] rounded-2xl shadow-2xl w-full max-w-2xl overflow-hidden animate-in fade-in zoom-in-95 duration-150">
        {/* Modal Header */}
        <div className="px-6 py-4 border-b border-gray-200 dark:border-[#212226] flex items-center justify-between">
          <div className="flex items-center gap-3">
            <span
              className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-bold border ${statusMeta.cls}`}
            >
              <StatusIcon className="w-4 h-4" />
              {statusMeta.text}
            </span>
            <div>
              <h3 className="text-base font-bold text-gray-900 dark:text-white">{subName}</h3>
              <p className="text-[11px] text-gray-500 dark:text-gray-400">
                Log ID: #{log.id ?? log.Id} • {reportLabel}
              </p>
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="p-2 rounded-xl text-gray-400 hover:text-gray-700 dark:hover:text-white hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Modal Body */}
        <div className="p-6 space-y-4 max-h-[75vh] overflow-y-auto text-xs">
          {/* Bilgi Grid */}
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
            <div className="p-3 rounded-xl bg-gray-50 dark:bg-[#111214] border border-gray-200 dark:border-[#212226]">
              <div className="text-[10px] font-semibold uppercase text-gray-400">Tetikleme Tipi</div>
              <div className="font-bold text-gray-900 dark:text-white mt-1">
                {isTest ? "SMTP Bağlantı Testi" : isManual ? "Manuel Tetikleme" : "Otomatik Zamanlayıcı (Cron)"}
              </div>
            </div>

            <div className="p-3 rounded-xl bg-gray-50 dark:bg-[#111214] border border-gray-200 dark:border-[#212226]">
              <div className="text-[10px] font-semibold uppercase text-gray-400">Rapor Dönemi</div>
              <div className="font-bold text-gray-900 dark:text-white mt-1">{parsedKey.periodText}</div>
            </div>

            <div className="p-3 rounded-xl bg-gray-50 dark:bg-[#111214] border border-gray-200 dark:border-[#212226]">
              <div className="text-[10px] font-semibold uppercase text-gray-400">Ek Dosya & Format</div>
              <div className="font-bold text-gray-900 dark:text-white mt-1 flex items-center gap-1.5">
                <span>
                  {attachmentCount > 0
                    ? `${attachmentCount} dosya (${formatBytes(attachmentBytes)})`
                    : "Ek Yok"}
                </span>
                {fmtBadge && (
                  <span className={`px-1.5 py-0.5 text-[10px] rounded border ${fmtBadge.cls}`}>
                    {fmtBadge.label}
                  </span>
                )}
              </div>
            </div>

            <div className="p-3 rounded-xl bg-gray-50 dark:bg-[#111214] border border-gray-200 dark:border-[#212226]">
              <div className="text-[10px] font-semibold uppercase text-gray-400">Başlangıç Zamanı</div>
              <div className="font-semibold text-gray-900 dark:text-white mt-1">
                {fmtDateTime(startedAt)}
              </div>
            </div>

            <div className="p-3 rounded-xl bg-gray-50 dark:bg-[#111214] border border-gray-200 dark:border-[#212226]">
              <div className="text-[10px] font-semibold uppercase text-gray-400">Bitiş Zamanı</div>
              <div className="font-semibold text-gray-900 dark:text-white mt-1">
                {fmtDateTime(completedAt)}
              </div>
            </div>

            <div className="p-3 rounded-xl bg-gray-50 dark:bg-[#111214] border border-gray-200 dark:border-[#212226]">
              <div className="text-[10px] font-semibold uppercase text-gray-400">Toplam Süre</div>
              <div className="font-bold text-emerald-600 dark:text-emerald-400 mt-1">{durationStr}</div>
            </div>
          </div>

          {/* Çalıştırma Kodu (RunKey) */}
          <div className="p-3.5 rounded-xl bg-gray-50 dark:bg-[#111214] border border-gray-200 dark:border-[#212226] flex items-center justify-between gap-3">
            <div className="min-w-0">
              <div className="text-[10px] font-semibold uppercase text-gray-400">
                Benzersiz Çalıştırma Anahtarı (Idempotency RunKey)
              </div>
              <div className="font-mono text-xs text-gray-800 dark:text-gray-200 break-all mt-1">
                {runKey || "-"}
              </div>
            </div>
            {runKey && (
              <button
                type="button"
                onClick={() => onCopy(runKey, "Çalıştırma anahtarı")}
                className="px-3 py-1.5 rounded-xl bg-white dark:bg-[#18191c] border border-gray-200 dark:border-[#212226] text-gray-700 dark:text-gray-200 font-semibold hover:bg-gray-100 shrink-0 inline-flex items-center gap-1.5"
              >
                <Copy className="w-3.5 h-3.5" />
                Kopyala
              </button>
            )}
          </div>

          {/* Alıcı Listesi */}
          <div className="p-4 rounded-xl bg-gray-50 dark:bg-[#111214] border border-gray-200 dark:border-[#212226] space-y-3">
            <div>
              <div className="text-[10px] font-semibold uppercase text-gray-400 mb-1.5">
                Alıcılar (To — {toList.length} Kişi)
              </div>
              {toList.length === 0 ? (
                <span className="text-gray-400">Kayıtlı alıcı yok</span>
              ) : (
                <div className="flex flex-wrap gap-1.5">
                  {toList.map((email) => (
                    <span
                      key={email}
                      className="px-2.5 py-1 rounded-lg bg-white dark:bg-[#18191c] border border-gray-200 dark:border-[#212226] text-gray-800 dark:text-gray-200 font-medium"
                    >
                      {email}
                    </span>
                  ))}
                </div>
              )}
            </div>

            {ccList.length > 0 && (
              <div className="pt-2 border-t border-gray-200 dark:border-[#212226]">
                <div className="text-[10px] font-semibold uppercase text-gray-400 mb-1.5">
                  Bilgi (CC — {ccList.length} Kişi)
                </div>
                <div className="flex flex-wrap gap-1.5">
                  {ccList.map((email) => (
                    <span
                      key={email}
                      className="px-2.5 py-1 rounded-lg bg-white dark:bg-[#18191c] border border-gray-200 dark:border-[#212226] text-gray-600 dark:text-gray-300"
                    >
                      {email}
                    </span>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Hata veya Sonuç Mesajı */}
          {err ? (
            <div
              className={`p-4 rounded-xl border ${
                statusMeta.key === "NoData"
                  ? "bg-amber-50/70 dark:bg-amber-950/30 border-amber-200 dark:border-amber-800/60 text-amber-800 dark:text-amber-200"
                  : "bg-red-50/70 dark:bg-red-950/30 border-red-200 dark:border-red-800/60 text-red-800 dark:text-red-200"
              }`}
            >
              <div className="font-bold mb-1 flex items-center gap-1.5">
                <StatusIcon className="w-4 h-4 shrink-0" />
                <span>
                  {statusMeta.key === "NoData"
                    ? "Rapor Döneminde Veri Bulunamadı"
                    : "Gönderim Hata Detayı"}
                </span>
              </div>
              <p className="leading-relaxed break-words">{err}</p>
            </div>
          ) : (
            <div className="p-4 rounded-xl bg-emerald-50/70 dark:bg-emerald-950/30 border border-emerald-200 dark:border-emerald-800/60 text-emerald-800 dark:text-emerald-200 flex items-center gap-2.5">
              <CheckCircle2 className="w-5 h-5 text-emerald-500 shrink-0" />
              <div>
                <div className="font-bold">Gönderim Başarıyla Tamamlandı</div>
                <div className="text-[11px] opacity-90 mt-0.5">
                  E-posta ve rapor ekleri SMTP sunucusu tarafından kabul edilerek alıcılara iletildi.
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Modal Footer */}
        <div className="px-6 py-4 bg-gray-50 dark:bg-[#111214] border-t border-gray-200 dark:border-[#212226] flex items-center justify-end gap-2">
          {canRun && subId > 0 && !subDeleted && (
            <button
              type="button"
              disabled={retryingSubId === subId}
              onClick={() => {
                onRetry(subId, subName);
                onClose();
              }}
              className="inline-flex items-center gap-1.5 px-4 py-2 rounded-xl text-xs font-semibold bg-emerald-600 hover:bg-emerald-700 text-white shadow-sm transition-colors"
            >
              <Play className="w-3.5 h-3.5" />
              Aboneliği Şimdi Tekrar Çalıştır
            </button>
          )}
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 rounded-xl text-xs font-semibold bg-white dark:bg-[#18191c] border border-gray-200 dark:border-[#212226] text-gray-700 dark:text-gray-300 hover:bg-gray-100"
          >
            Kapat
          </button>
        </div>
      </div>
    </div>
  );
}
